import React from 'react';

interface AIGeometricIconProps {
  size?: number;
  color?: string;
  className?: string;
  animate?: boolean;
}

const AIGeometricIcon: React.FC<AIGeometricIconProps> = ({ 
  size = 24, 
  color = 'currentColor',
  className = '',
  animate = false
}) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`ai-geometric-icon ${animate ? 'animate' : ''} ${className}`}
    >
      {/* Outer hexagon */}
      <path
        d="M12 2 L20 7 L20 17 L12 22 L4 17 L4 7 Z"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        opacity="0.6"
      >
        {animate && (
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="3s"
            repeatCount="indefinite"
          />
        )}
      </path>

      {/* Inner geometric pattern */}
      <path
        d="M12 6 L16 9 L16 15 L12 18 L8 15 L8 9 Z"
        stroke={color}
        strokeWidth="1.2"
        fill="none"
        opacity="0.8"
      >
        {animate && (
          <animate
            attributeName="opacity"
            values="0.8;1;0.8"
            dur="2.5s"
            repeatCount="indefinite"
          />
        )}
      </path>

      {/* Central core */}
      <circle
        cx="12"
        cy="12"
        r="3"
        fill={color}
        opacity="0.3"
      >
        {animate && (
          <animate
            attributeName="opacity"
            values="0.3;0.7;0.3"
            dur="2s"
            repeatCount="indefinite"
          />
        )}
      </circle>

      {/* Data nodes */}
      <circle cx="12" cy="6" r="1.5" fill={color} opacity="0.8">
        {animate && (
          <animate
            attributeName="r"
            values="1.5;2;1.5"
            dur="2.2s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="16" cy="9" r="1.2" fill={color} opacity="0.7">
        {animate && (
          <animate
            attributeName="r"
            values="1.2;1.7;1.2"
            dur="1.8s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="16" cy="15" r="1.2" fill={color} opacity="0.7">
        {animate && (
          <animate
            attributeName="r"
            values="1.2;1.7;1.2"
            dur="2.4s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="12" cy="18" r="1.5" fill={color} opacity="0.8">
        {animate && (
          <animate
            attributeName="r"
            values="1.5;2;1.5"
            dur="1.9s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="8" cy="15" r="1.2" fill={color} opacity="0.7">
        {animate && (
          <animate
            attributeName="r"
            values="1.2;1.7;1.2"
            dur="2.1s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="8" cy="9" r="1.2" fill={color} opacity="0.7">
        {animate && (
          <animate
            attributeName="r"
            values="1.2;1.7;1.2"
            dur="2.3s"
            repeatCount="indefinite"
          />
        )}
      </circle>

      {/* Connection lines */}
      <g stroke={color} strokeWidth="0.8" opacity="0.4">
        <line x1="12" y1="6" x2="12" y2="12">
          {animate && (
            <animate
              attributeName="opacity"
              values="0.4;0.8;0.4"
              dur="3s"
              repeatCount="indefinite"
            />
          )}
        </line>
        <line x1="16" y1="9" x2="12" y2="12">
          {animate && (
            <animate
              attributeName="opacity"
              values="0.4;0.8;0.4"
              dur="2.7s"
              repeatCount="indefinite"
            />
          )}
        </line>
        <line x1="16" y1="15" x2="12" y2="12">
          {animate && (
            <animate
              attributeName="opacity"
              values="0.4;0.8;0.4"
              dur="2.9s"
              repeatCount="indefinite"
            />
          )}
        </line>
        <line x1="12" y1="18" x2="12" y2="12">
          {animate && (
            <animate
              attributeName="opacity"
              values="0.4;0.8;0.4"
              dur="3.2s"
              repeatCount="indefinite"
            />
          )}
        </line>
        <line x1="8" y1="15" x2="12" y2="12">
          {animate && (
            <animate
              attributeName="opacity"
              values="0.4;0.8;0.4"
              dur="2.6s"
              repeatCount="indefinite"
            />
          )}
        </line>
        <line x1="8" y1="9" x2="12" y2="12">
          {animate && (
            <animate
              attributeName="opacity"
              values="0.4;0.8;0.4"
              dur="3.1s"
              repeatCount="indefinite"
            />
          )}
        </line>
      </g>

      {/* Processing rings */}
      <circle
        cx="12"
        cy="12"
        r="5"
        fill="none"
        stroke={color}
        strokeWidth="0.5"
        opacity="0.2"
        strokeDasharray="3,2"
      >
        {animate && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 12 12;360 12 12"
            dur="8s"
            repeatCount="indefinite"
          />
        )}
      </circle>

      <circle
        cx="12"
        cy="12"
        r="7"
        fill="none"
        stroke={color}
        strokeWidth="0.3"
        opacity="0.15"
        strokeDasharray="2,3"
      >
        {animate && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="360 12 12;0 12 12"
            dur="12s"
            repeatCount="indefinite"
          />
        )}
      </circle>

      {/* Central AI indicator */}
      <text
        x="12"
        y="13"
        textAnchor="middle"
        fontSize="4"
        fill={color}
        opacity="0.6"
        fontFamily="monospace"
        fontWeight="bold"
      >
        AI
        {animate && (
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="1.5s"
            repeatCount="indefinite"
          />
        )}
      </text>
    </svg>
  );
};

export default AIGeometricIcon;









